/**
 * Global Store - Zustand
 * Manages application state
 */

import { create } from 'zustand'
import { PHASES } from '@agents/orchestrator'
import { isDevMode } from '@services/llm'

export const useStore = create((set, get) => ({
  // Project state
  project: {
    domain: null,
    phase: PHASES.INIT,
    context: {},
  },

  // Generated files
  files: {},
  selectedFile: null,

  // Generated images (from Images agent)
  images: [],

  // Link placements tracking (money site + resource links)
  linkPlacements: null, // { moneySite: { page, url }, resourceLinks: [{ url, pages }] }

  // Last error for debug/support
  lastError: null, // { message, phase, timestamp }

  // Messages/chat
  messages: [],

  // Pipeline progress
  progress: {
    phase: null,
    status: 'idle',
    message: '',
    percentage: 0,
  },

  // AI-generated summaries per phase (nudge marketing)
  summaries: {},

  // Generation stats
  stats: {
    startTime: null,
    endTime: null,
    totalTokens: 0,
  },

  // UI state
  ui: {
    rightPanel: 'pipeline', // 'pipeline' (Agents IA) | 'pages' | 'preview'
    previewMode: 'code', // 'code' | 'preview'
    isLoading: false,
    debugMode: isDevMode(), // Sync with localStorage on init
  },

  // Settings
  settings: {
    provider: 'claude',
    model: 'sonnet',
    // Dual-model configuration: different LLMs for reasoning vs production
    useDualModel: false,  // Toggle for dual-model mode
    reasoningModel: 'sonnet',  // For SERP, Stratège, Identité, Architecte, Rédacteur, DA
    productionModel: 'opus',   // For Content Writer (HTML generation)
    // Cross-provider mode (anti-fingerprint): use different providers for reasoning vs production
    reasoningProvider: null,   // If set, use this provider for reasoning phases
    productionProvider: null,  // If set, use this provider for production phases
    // Search provider for SERP analysis (perplexity | claude | openai | gemini | multi | auto)
    searchProvider: 'auto',    // 'auto' = best available, 'multi' = random rotation
    autoMode: true,
    cmsPattern: 'tailwind', // tailwind | wordpress | webflow | drupal | joomla
  },

  // Credits
  credits: 0,

  // Actions
  setDomain: (domain) => set((state) => ({
    project: { ...state.project, domain },
  })),

  setPhase: (phase) => set((state) => ({
    project: { ...state.project, phase },
  })),

  setContext: (context) => set((state) => ({
    project: { ...state.project, context: { ...state.project.context, ...context } },
  })),

  addFile: (name, content) => set((state) => ({
    files: { ...state.files, [name]: content },
  })),

  setFiles: (files) => set({ files }),

  setImages: (images) => set({ images }),

  setLinkPlacements: (linkPlacements) => set({ linkPlacements }),

  selectFile: (name) => set({ selectedFile: name }),

  addMessage: (message) => set((state) => ({
    messages: [...state.messages, { id: Date.now(), ...message }],
  })),

  // Update an existing agent message (find by agent phase, update with new data)
  updateAgentMessage: (agent, updates) => set((state) => {
    const messages = [...state.messages]
    // Find the last message for this agent
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'agent' && messages[i].agent === agent) {
        messages[i] = { ...messages[i], ...updates }
        break
      }
    }
    return { messages }
  }),

  clearMessages: () => set({ messages: [] }),

  setProgress: (progress) => set((state) => ({
    progress: { ...state.progress, ...progress },
  })),

  setSummary: (phase, summary) => set((state) => ({
    summaries: { ...state.summaries, [phase]: summary },
  })),

  clearSummaries: () => set({ summaries: {} }),

  setStats: (stats) => set((state) => ({
    stats: { ...state.stats, ...stats },
  })),

  setLoading: (isLoading) => set((state) => ({
    ui: { ...state.ui, isLoading },
  })),

  setRightPanel: (panel) => set((state) => ({
    ui: { ...state.ui, rightPanel: panel },
  })),

  setPreviewMode: (mode) => set((state) => ({
    ui: { ...state.ui, previewMode: mode },
  })),

  setDebugMode: (enabled) => set((state) => ({
    ui: { ...state.ui, debugMode: enabled },
  })),

  setSettings: (settings) => set((state) => ({
    settings: { ...state.settings, ...settings },
  })),

  setCredits: (credits) => set({ credits }),

  // Track errors for debug/support
  setLastError: (error) => set({
    lastError: error ? {
      message: error.message || String(error),
      phase: error.phase || null,
      timestamp: new Date().toISOString(),
    } : null,
  }),

  clearLastError: () => set({ lastError: null }),

  // Reset project
  resetProject: () => set({
    project: { domain: null, phase: PHASES.INIT, context: {} },
    files: {},
    images: [],
    linkPlacements: null,
    lastError: null,
    selectedFile: null,
    messages: [],
    progress: { phase: null, status: 'idle', message: '', percentage: 0 },
    summaries: {},
    stats: { startTime: null, endTime: null, totalTokens: 0 },
  }),

  // Export project state
  exportState: () => {
    const state = get()
    return {
      project: state.project,
      files: state.files,
      messages: state.messages,
      timestamp: new Date().toISOString(),
    }
  },

  // Import project state
  importState: (saved) => set({
    project: saved.project,
    files: saved.files || {},
    messages: saved.messages || [],
  }),
}))

export default useStore
